import { lowMemoryMode } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils';
export interface canvasPoolManagerOptions {
    memoryLimit?: number;
    autoClear?: boolean;
}
export const Priority = {
    LOW: -10000,         // 低优先级
    NORMAL: 0,    // 正常优先级
    HIGH: 10000,    // 高优先级
};

export interface CanvasObject {
    id: string;
    instance: HTMLCanvasElement;
    inUse: boolean;
    onForceFree?: () => void;
    memory: number;
    usingTime: number;
}

interface Applier {
    resolver: (value: CanvasObject) => void;
    option: ApplyCanvasOptions;
}

interface ApplyCanvasOptions {
    memory: number;
    priority?: number;
    onForceFree?: () => void;
}
const LOW_MEMORY_LIMIT = 50 * 1024 * 1024;
const NORMAL_MEMORY_LIMIT = 1024 * 1024 * 1024;
export class CanvasPoolManager {
    protected static instance?: CanvasPoolManager;
    public static getInstance(): CanvasPoolManager {
        if (!CanvasPoolManager.instance) {
            // TODO 这里要改成entry传入配置，直接从配置读，不在这里判断移动端
            CanvasPoolManager.instance = new CanvasPoolManager({
                memoryLimit: lowMemoryMode ? LOW_MEMORY_LIMIT : NORMAL_MEMORY_LIMIT,
            });
        }
        return CanvasPoolManager.instance;
    }

    // 这里的canvasPool和任务队列不同，队列的等待和执行队列存放的是一次性的任务，所以用两个队列存和推出比较合适
    // 这里是一个canvas的池子，用两个数据存储使用和闲置有一些浪费。而且池子中的对象基本不变，只是使用/闲置状态变更。
    // 所以这里用一个状态标识状态，而不是两个set进行维护
    private canvasPool: Set<CanvasObject>;
    private inQueueApplier: Array<Applier>;

    private deliverPromise: Promise<unknown>;
    private memoryInUse: number;
    private memoryLimit: number;
    private autoClear: boolean;
    constructor(props?: canvasPoolManagerOptions) {
        const { memoryLimit = 50 * 1024 * 1024, autoClear = true } = props || {};
        this.canvasPool = new Set<CanvasObject>();
        this.inQueueApplier = [];
        this.memoryLimit = memoryLimit;
        this.autoClear = autoClear;
        this.memoryInUse = 0;
    }

    // 申请使用一个canvas节点
    public applyCanvas(options: ApplyCanvasOptions): Promise<CanvasObject> {
        const availableCanvasObject = this.getAvailableCanvasObject(options);
        if (availableCanvasObject) {
            this.markCanvasObjectInUse(availableCanvasObject, options);
            return Promise.resolve(availableCanvasObject);
        }

        const applyPromise: Promise<CanvasObject> = new Promise((resolve) => {
            this.inQueueApplier.push({
                resolver: resolve,
                option: {
                    priority: Priority.NORMAL,
                    ...options,
                },
            });
        });

        return applyPromise;
    }

    // 主动释放当前的canvas节点，交给下一个申请者使用
    public releaseCanvas(canvasObject: CanvasObject) {
        const { applier: nextApplier, index } = this.getNextApplier();

        if (nextApplier && this.shouldResolveCanvasObjectToNextApplier(nextApplier, canvasObject)) {
            this.inQueueApplier.splice(index, 1);
            // 如果有下一个说明还需要，按配置autoClear进行清除
            // 比如g2每次绘制会自动设置宽高，就可以配置不autoClear，来节省一次resize开销
            this.resetCanvas(canvasObject, this.autoClear);
            this.markCanvasObjectInUse(canvasObject, nextApplier.option);
            nextApplier.resolver(canvasObject);
        } else {
            // 如果没下一个，按清除画布，释放canvas内存
            this.resetCanvas(canvasObject, true);
        }
    }

    private shouldResolveCanvasObjectToNextApplier(nextApplier: Applier, canvasObject: CanvasObject) {
        // 当有canvas释放时，如果有下一个申请者
        // 如果会超内存，且有正在使用的canvas，就等待。将判定交给下一个完成的canvas
        // 否则直接提供
        const { memory } = nextApplier.option;
        const willOutOfMemory = memory + this.memoryInUse > this.memoryLimit;

        if (willOutOfMemory && !this.isAllIdel()) {
            return false;
        }

        return true;
    }

    // 寻找一个申请者，通知释放canvas
    private findApplierToFreeCanvas() {
        this.deliverPromise = (this.deliverPromise || Promise.resolve()).then(async () =>  {
            const earliestCanvasObject = this.getEarliestCanvas();

            if (earliestCanvasObject) {
                await this.forceApplierToFreeCanvas(earliestCanvasObject);

                this.releaseCanvas(earliestCanvasObject);
            }
        });
    }

    private async forceApplierToFreeCanvas(canvasObject: CanvasObject): Promise<void> {
        // todo 这里后面看看如何处理失败的情况
        await canvasObject.onForceFree?.();
    }

    private getAvailableCanvasObject(options: ApplyCanvasOptions): CanvasObject | null {
        const willOutOfMemory = this.memoryInUse + options.memory >= this.memoryLimit;
        const idleCanvasObject = this.getIdleCanvasObject();
        const hasUsingCanvasObject = this.canvasPool.size >= 0 && !this.isAllIdel();

        // 如果会超出内存，但是此时没有正在使用的canvas，就使用存在的空闲canvas，或者创建一个新的
        // 如果有正在使用的canvas，就等这个canvas结束之后，再去获取canvasObject
        if (willOutOfMemory && hasUsingCanvasObject) {
            return null;
        }

        // 有空闲实例就使用
        if (idleCanvasObject) {
            return idleCanvasObject;
        }

        // 没有实例 或者 实例没有占满就 创建一个到池子，实现懒创建
        const canvasObject = this.createCanvasObject(options);
        this.canvasPool.add(canvasObject);

        return canvasObject;
    }

    private createCanvasObject(applyOption: ApplyCanvasOptions): CanvasObject {
        const id = `${Math.random().toString(16)}-${Date.now()}`;
        const canvasElement = document.createElement('canvas');

        return {
            id,
            instance: canvasElement,
            inUse: false,
            onForceFree: applyOption.onForceFree,
            memory: applyOption.memory,
            usingTime: 0,
        };
    }

    private isAllIdel(exceptCanvas?: CanvasObject): boolean {
        let isAllIdle = true;
        this.canvasPool.forEach((canvas, index) => {
            if (canvas.inUse && canvas.id !== exceptCanvas?.id) {
                isAllIdle = false;
            }
        });

        return isAllIdle;
    }

    // 获取当前空闲的canvasObject
    private getIdleCanvasObject(): CanvasObject | null {
        let canvasObject: CanvasObject | null = null;

        for (const object of this.canvasPool) {
            if (!object.inUse) {
                canvasObject = object;
                break;
            }
        }

        return canvasObject;
    }

    private resetCanvas(canvasObject: CanvasObject, autoClear = true) {
        canvasObject.inUse = false;
        this.releaseMemory(canvasObject);

        if (autoClear) {
            // canvas大小改变之后一定会重绘，就直接白了，符合预期
            canvasObject.instance.height = 1;
            canvasObject.instance.width = 1;
        }
    }

    private getNextApplier(): { applier: Applier|null, index: number } {
        let nextApplier: Applier | null = null;
        let applierIndex = -1;

        this.inQueueApplier.forEach((applier, index) => {
            if (!nextApplier || applier.option.priority! > (nextApplier as Applier).option.priority!) {
                nextApplier = applier;
                applierIndex = index;
            }
        });

        return { applier: nextApplier, index: applierIndex };
    }

    private getEarliestCanvas(): CanvasObject | null {
        let targetCanvas: CanvasObject | null = null;

        this.canvasPool.forEach((canvas, index) => {
            if (!targetCanvas || canvas.usingTime < targetCanvas.usingTime) {
                targetCanvas = canvas;
            }
        });

        return targetCanvas;
    }

    private markCanvasObjectInUse(canvasObject: CanvasObject, applyCanvasOptions: ApplyCanvasOptions) {
        canvasObject.inUse = true;
        canvasObject.usingTime = Date.now();
        const { memory, onForceFree } = applyCanvasOptions;
        canvasObject.memory = memory;
        canvasObject.onForceFree = onForceFree;

        this.increaseMemory(canvasObject);
    }

    private increaseMemory(canvasObject: CanvasObject) {
        this.memoryInUse += canvasObject.memory;
    }

    private releaseMemory(canvasObject: CanvasObject) {
        this.memoryInUse -= canvasObject.memory;
        // 理论上是4，这里先忽略
        canvasObject.memory = 0;
    }
}
